import React, { useState, useEffect, useCallback } from "react";
import { cronLogService } from "../../../service/admin/cronLogService";
import { formatDate } from "../../../helper/utils";
import Loader from "../../../component/common/loader";
import { FaSync, FaEye, FaTrash, FaPlay, FaCheckCircle, FaTimesCircle, FaSpinner, FaClock, FaExclamationTriangle, FaStop, FaUndo, FaBarcode } from "react-icons/fa";

const STATUS_COLORS = {
  running: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const STATUS_ICONS = {
  running: <FaSpinner className="animate-spin" />,
  completed: <FaCheckCircle />,
  failed: <FaTimesCircle />,
  cancelled: <FaClock />,
};

const JOB_TYPE_LABELS = {
  "pricing-update-all": "Full Pricing Update",
  "pricing-update-category": "Category Update",
  "pricing-update-sku": "SKU Update",
  "other": "Other",
  "create-order": "Create Order",
  "order-detail": "Order Detail",
  "logistics-info": "Logistics Info",
  "logistics-trace": "Logistics Trace",
  "product-detail-by-offerid": "Product Detail by Offer ID",
  "get-category-by-category-id": "Get Category by Category ID",
  "generate-description": "Generate Description",
  "product-freight-cost": "Product Freight Cost",
  "get-product-info": "Get Product Info"
};

const JOB_TYPE_GROUPS = {
  global: {
    label: "Global Job",
    types: [
      'create-order', 'order-detail', 'logistics-info', 'logistics-trace', 'product-detail-by-offerid',
      'get-category-by-category-id', 'generate-description', 'product-freight-cost', 'get-product-info'
    ]
  },
  pricing: {
    label: "Pricing Update",
    types: ["pricing-update-all", "pricing-update-category", "pricing-update-sku"]
  },
  other: {
    label: "Other",
    types: ["other"]
  }
};

// Confirmation Modal Component
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirm", confirmColor = "blue" }) => {
  if (!isOpen) return null;
  const colorClasses = {
    blue: "bg-blue-500 hover:bg-blue-600",
    red: "bg-red-500 hover:bg-red-600",
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <FaExclamationTriangle className="text-yellow-500 text-xl" />
          <h3 className="text-lg font-bold">{title}</h3>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-white rounded ${colorClasses[confirmColor]}`}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
};

// Result Modal Component
const ResultModal = ({ isOpen, title, message, type = "success", onClose }) => {
  if (!isOpen) return null;
  const icons = {
    success: <FaCheckCircle className="text-green-500 text-4xl" />,
    error: <FaTimesCircle className="text-red-500 text-4xl" />,
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 text-center">
        <div className="mb-4">{icons[type]}</div>
        <h3 className="text-lg font-bold mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <button onClick={onClose} className="px-6 py-2 bg-gray-100 rounded hover:bg-gray-200">Close</button>
      </div>
    </div>
  );
};


export default function CronLogs() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [groupedLogs, setGroupedLogs] = useState({});
  const [availableDates, setAvailableDates] = useState([]);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedTypes, setExpandedTypes] = useState(new Set()); // Format: "date|type"
  const [nestedLoading, setNestedLoading] = useState({}); // Format: { "date|type": boolean }
  const [nestedJobTypePages, setNestedJobTypePages] = useState({}); // { "date|type": { page, total, pages, limit } }
  const [jobTypeCountsByDate, setJobTypeCountsByDate] = useState({}); // Store individual jobType counts from API
  const [pagination, setPagination] = useState({
    page: 1, limit: 5, total: 0, pages: 0,
    jobTypePage: 1, jobTypeLimit: 5, jobTypeTotal: 0, jobTypePages: 0
  });
  const [filters, setFilters] = useState({ status: "", parentJob: "", jobType: "", startDate: "", endDate: "" });
  const [pendingFilters, setPendingFilters] = useState({ status: "", parentJob: "", jobType: "", startDate: "", endDate: "" });

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: null, confirmText: "", confirmColor: "blue" });
  const [resultModal, setResultModal] = useState({ isOpen: false, title: "", message: "", type: "success" });
  const [pricingStatus, setPricingStatus] = useState(null);
  const [runUpdateModal, setRunUpdateModal] = useState({ isOpen: false, algorithms: [], domains: [], selectedDomain: "", selectedAlgorithm: null, remark: "" });
  const [skuUpdateModal, setSkuUpdateModal] = useState({ isOpen: false, skuInput: "", skuList: [], algorithms: [], selectedAlgorithm: null, remark: "" });
  const [hoveredRemark, setHoveredRemark] = useState(null); // { remark, top, left }

  const fetchPricingStatus = async () => {
    try {
      const response = await cronLogService.getPricingStatus();
      if (response?.success) setPricingStatus(response.status);
    } catch (error) {
      console.error("Error fetching pricing status:", error);
    }
  };

  const fetchAlgorithmsAndDomains = async () => {
    try {
      const [algosRes, domainsRes] = await Promise.all([
        cronLogService.getPricingAlgorithms(),
        cronLogService.getSupportedDomains(),
      ]);
      const algorithms = algosRes?.status?.code === 200 ? algosRes.data : [];
      const domains = domainsRes?.status?.code === 200 ? domainsRes.data : [];
      return { algorithms, domains };
    } catch (error) {
      console.error("Error fetching algorithms:", error);
      return { algorithms: [], domains: [] };
    }
  };

  const fetchLogs = useCallback(async (showLoader = false, overrideFilters = null) => {
    if (showLoader) setLoading(true);
    try {
      const activeFilters = overrideFilters || filters;
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        jobTypePage: pagination.jobTypePage,
        jobTypeLimit: pagination.jobTypeLimit,
        groupByDate: "true",
        sortBy: pagination.sortBy || "createdAt",
        sortOrder: pagination.sortOrder || "desc",
        ...activeFilters
      };

      // Clean up empty params
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await cronLogService.getLogs(params);
      if (response?.status?.code === 200) {
        setGroupedLogs(response.data.groupedByDate || {});
        setAvailableDates(response.data.dates || []);
        setJobTypeCountsByDate(response.data.jobTypeCountsByDate || {}); // Store jobType counts
        setPagination((prev) => ({
          ...prev,
          ...response.data.pagination,
          // keep jobTypeLimit as a configurable page-size for nested lists,
          // but do not rely on global jobType* values for rendering, as
          // nested pagination is handled per jobType section.
          jobTypeLimit: response.data.jobTypePagination?.limit ?? prev.jobTypeLimit
        }));

        // reset nested pagination meta when main list is re-fetched
        setNestedJobTypePages({});

        // Auto-expand the first date if it's a new fetch
        if (response.data.dates?.length > 0 && expandedDates.size === 0) {
          setExpandedDates(new Set([response.data.dates[0]]));
        }
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [pagination.page, pagination.limit, pagination.jobTypePage, pagination.jobTypeLimit, pagination.sortBy, pagination.sortOrder, filters]);

  const fetchNestedLogs = useCallback(async (date, jobType, logPage = 1) => {
    const typeKey = `${date}|${jobType}`;
    setNestedLoading(prev => ({ ...prev, [typeKey]: true }));
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        groupByDate: "true",
        sortBy: pagination.sortBy || "createdAt",
        sortOrder: pagination.sortOrder || "desc",
        ...filters,
        date,  // specify the date for single-date mode
        jobType,  // specify the jobType for log pagination
        logPage,  // pagination for logs within this jobType
        logLimit: pagination.jobTypeLimit || 5,  // use same limit for logs
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) delete params[key];
      });
      const response = await cronLogService.getLogs(params);
      if (response?.status?.code === 200) {
        const dateData = response.data.groupedByDate?.[date] || {};
        const logs = dateData[jobType] || [];
        const logPag = response.data.pagination || {};

        // Get pagination metadata from response
        const currentPage = logPag.logPage || logPage;
        const totalPages = logPag.logPages || 1;
        const totalLogs = logPag.logTotal || 0;
        const limit = logPag.logLimit || pagination.jobTypeLimit || 5;

        // If the API returns no data, keep current UI as-is
        if (logs.length === 0 && !totalLogs && currentPage === 1) return;

        // Update grouped logs for this specific date & jobType with
        // the fresh page from the API.
        setGroupedLogs(prev => {
          const prevDateData = prev[date] || {};
          return {
            ...prev,
            [date]: {
              ...prevDateData,
              [jobType]: logs
            }
          };
        });

        // Store nested pagination info per date|jobType so that
        // each nested table paginates independently using the
        // log pagination values from the API.
        setNestedJobTypePages(prev => ({
          ...prev,
          [typeKey]: {
            page: currentPage,
            total: totalLogs,
            pages: totalPages,
            limit: limit
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching nested logs:", error);
    } finally {
      setNestedLoading(prev => ({ ...prev, [typeKey]: false }));
    }
  }, [pagination.page, pagination.limit, pagination.jobTypeLimit, pagination.sortBy, pagination.sortOrder, filters]);

  const fetchStats = async () => {
    try {
      const response = await cronLogService.getStats();
      if (response?.status?.code === 200) setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchLogDetails = async (id) => {
    setDetailsLoading(true);
    try {
      const response = await cronLogService.getLogDetails(id);
      if (response?.status?.code === 200) setSelectedLog(response.data);
    } catch (error) {
      console.error("Error fetching log details:", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchLogs(false), fetchStats(), fetchPricingStatus()]);
    setLoading(false);
  };

  const handleCleanup = () => {
    setConfirmModal({
      isOpen: true, title: "Cleanup Old Logs",
      message: "This will delete all cron logs older than 30 days. This action cannot be undone.",
      confirmText: "Delete Old Logs", confirmColor: "red",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          const response = await cronLogService.cleanup(30, false);
          if (response?.status?.code === 200) {
            const count = response.data.deletedCount;
            const message = count > 0
              ? `Deleted ${count} old logs.`
              : "No logs older than 30 days found.";
            setResultModal({ isOpen: true, title: "Cleanup Complete", message, type: "success" });
            handleRefresh();
          }
        } catch (error) {
          setResultModal({ isOpen: true, title: "Cleanup Failed", message: error.message || "Failed to cleanup logs", type: "error" });
        }
      },
    });
  };

  const handleDeleteAllLogs = () => {
    setConfirmModal({
      isOpen: true, title: "Delete All Logs",
      message: "This will delete ALL cron logs (except running jobs). This action cannot be undone. Are you sure?",
      confirmText: "Delete All", confirmColor: "red",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          const response = await cronLogService.cleanup(0, true);
          if (response?.status?.code === 200) {
            setResultModal({ isOpen: true, title: "All Logs Deleted", message: `Deleted ${response.data.deletedCount} logs.`, type: "success" });
            handleRefresh();
          }
        } catch (error) {
          setResultModal({ isOpen: true, title: "Delete Failed", message: error.message || "Failed to delete logs", type: "error" });
        }
      },
    });
  };

  const handleDeleteLog = (logId, logStatus) => {
    if (logStatus === 'running') {
      setResultModal({ isOpen: true, title: "Cannot Delete", message: "Cannot delete a running job. Stop it first.", type: "error" });
      return;
    }
    setConfirmModal({
      isOpen: true, title: "Delete Log",
      message: "Are you sure you want to delete this log entry?",
      confirmText: "Delete", confirmColor: "red",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          const response = await cronLogService.deleteLog(logId);
          if (response?.status?.code === 200) {
            setResultModal({ isOpen: true, title: "Log Deleted", message: "Log entry deleted successfully.", type: "success" });
            handleRefresh();
          }
        } catch (error) {
          setResultModal({ isOpen: true, title: "Delete Failed", message: error.message || "Failed to delete log", type: "error" });
        }
      },
    });
  };

  const handleTriggerUpdate = async () => {
    // Fetch algorithms and domains, then show selection modal
    const { algorithms, domains } = await fetchAlgorithmsAndDomains();

    // Filter only active common algorithms
    const activeAlgos = algorithms.filter(a => a.active && !a.deleted && a.scope === 'common');
    const defaultDomain = domains[0] || 'importerr.com';
    const domainAlgos = activeAlgos.filter(a => a.domain === defaultDomain);

    setRunUpdateModal({
      isOpen: true,
      algorithms: activeAlgos,
      domains,
      selectedDomain: defaultDomain,
      selectedAlgorithm: domainAlgos[0] || null, // Auto-select first algorithm for domain
      remark: "",
    });
  };

  const handleRunSelectedUpdate = async () => {
    const { selectedDomain, selectedAlgorithm, remark } = runUpdateModal;

    if (!selectedAlgorithm) {
      setResultModal({ isOpen: true, title: "Selection Required", message: "Please select an algorithm before starting the update.", type: "error" });
      return;
    }

    if (!remark || remark.trim() === "") {
      setResultModal({ isOpen: true, title: "Remark Required", message: "Please enter a remark/reason for this pricing update.", type: "error" });
      return;
    }

    setRunUpdateModal((m) => ({ ...m, isOpen: false }));

    try {
      const params = {
        async: true,
        domain: selectedDomain,
        algorithmId: selectedAlgorithm._id,
        remark: remark.trim(),
      };

      const response = await cronLogService.triggerPricingUpdateAll(params);
      if (response?.status?.code === 202 || response?.success) {
        // Enable auto-refresh and immediately fetch updated data
        setAutoRefresh(true);

        // Immediately refresh logs and stats to show the new running job
        await Promise.all([fetchLogs(), fetchStats(), fetchPricingStatus()]);

        setResultModal({
          isOpen: true,
          title: "Update Started",
          message: `Pricing update started for ${selectedDomain} using "${selectedAlgorithm.name}". Monitor progress in the logs.`,
          type: "success",
        });
      } else {
        setResultModal({ isOpen: true, title: "Failed", message: response?.message || "Failed to trigger update", type: "error" });
      }
    } catch (error) {
      setResultModal({ isOpen: true, title: "Failed", message: error.message || "Failed to trigger update", type: "error" });
    }
  };

  // SKU Update handlers
  const handleSkuUpdate = async () => {
    const { algorithms } = await fetchAlgorithmsAndDomains();
    const activeAlgos = algorithms.filter(a => a.active && !a.deleted && a.scope === 'common');

    setSkuUpdateModal({
      isOpen: true,
      skuInput: "",
      skuList: [],
      algorithms: activeAlgos,
      selectedAlgorithm: activeAlgos[0] || null,
      remark: "",
    });
  };

  const handleSkuInputChange = (value) => {
    setSkuUpdateModal(prev => {
      // Parse SKUs from input (comma, newline, or space separated)
      const skus = value
        .split(/[\s,\n]+/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
      return { ...prev, skuInput: value, skuList: [...new Set(skus)] };
    });
  };

  const handleRunSkuUpdate = async () => {
    const { skuList, selectedAlgorithm, remark } = skuUpdateModal;

    if (skuList.length === 0) {
      setResultModal({ isOpen: true, title: "No SKUs", message: "Please enter at least one SKU to update.", type: "error" });
      return;
    }

    if (!selectedAlgorithm) {
      setResultModal({ isOpen: true, title: "Selection Required", message: "Please select an algorithm.", type: "error" });
      return;
    }

    if (!remark || remark.trim() === "") {
      setResultModal({ isOpen: true, title: "Remark Required", message: "Please enter a remark/reason for this SKU update.", type: "error" });
      return;
    }

    setSkuUpdateModal(prev => ({ ...prev, isOpen: false }));

    try {
      const response = await cronLogService.triggerPricingUpdateSkus({
        skus: skuList,
        algorithmId: selectedAlgorithm._id,
        remark: remark.trim(),
      });

      if (response?.status?.code === 200 || response?.success) {
        await Promise.all([fetchLogs(), fetchStats(), fetchPricingStatus()]);

        setResultModal({
          isOpen: true,
          title: "SKU Update Complete",
          message: `Updated pricing for ${skuList.length} SKU(s). Check logs for details.`,
          type: "success",
        });
      } else {
        setResultModal({ isOpen: true, title: "Failed", message: response?.message || "Failed to update SKUs", type: "error" });
      }
    } catch (error) {
      setResultModal({ isOpen: true, title: "Failed", message: error.message || "Failed to update SKUs", type: "error" });
    }
  };

  useEffect(() => { handleRefresh(); }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    let interval;
    if (autoRefresh) interval = setInterval(() => { fetchLogs(); fetchStats(); fetchPricingStatus(); }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  const handleStopJob = (withRollback = false) => {
    setConfirmModal({
      isOpen: true,
      title: withRollback ? "Stop & Rollback" : "Stop Job",
      message: withRollback
        ? "This will stop the running job and rollback all updated prices to their original values. Are you sure?"
        : "This will stop the running job. Already updated products will keep their new prices. Are you sure?",
      confirmText: withRollback ? "Stop & Rollback" : "Stop Job",
      confirmColor: "red",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          const response = await cronLogService.cancelPricingUpdate(withRollback);
          if (response?.success) {
            // Immediately refresh logs and stats
            await Promise.all([fetchLogs(), fetchStats(), fetchPricingStatus()]);

            setResultModal({
              isOpen: true,
              title: "Job Stopped",
              message: withRollback
                ? "Job stopped. Rollback is in progress..."
                : "Job stopped successfully. Already updated products will keep their new prices.",
              type: "success",
            });
          } else {
            setResultModal({ isOpen: true, title: "Failed", message: response?.message || "Failed to stop job", type: "error" });
          }
        } catch (error) {
          setResultModal({ isOpen: true, title: "Failed", message: error.message || "Failed to stop job", type: "error" });
        }
      },
    });
  };

  const handleRollback = () => {
    setConfirmModal({
      isOpen: true,
      title: "Rollback Prices",
      message: `This will restore ${pricingStatus?.rollbackCount || 0} products to their original prices. Are you sure?`,
      confirmText: "Rollback",
      confirmColor: "red",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          const response = await cronLogService.rollbackPrices();
          if (response?.success) {
            // Immediately refresh logs and stats
            await Promise.all([fetchLogs(), fetchStats(), fetchPricingStatus()]);

            setResultModal({ isOpen: true, title: "Rollback Complete", message: response.message, type: "success" });
          } else {
            setResultModal({ isOpen: true, title: "Rollback Failed", message: response?.message || "Failed to rollback", type: "error" });
          }
        } catch (error) {
          setResultModal({ isOpen: true, title: "Rollback Failed", message: error.message || "Failed to rollback", type: "error" });
        }
      },
    });
  };

  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const toggleType = (date, type) => {
    const key = `${date}|${type}`;
    setExpandedTypes(prev => {
      const next = new Set(prev);
      const isCurrentlyExpanded = next.has(key);

      if (isCurrentlyExpanded) {
        next.delete(key);
      } else {
        next.add(key);
        // Fetch logs for this jobType when expanding for the first time
        // or when we don't have pagination data for this jobType
        if (!nestedJobTypePages[key] || nestedJobTypePages[key].page === 1) {
          fetchNestedLogs(date, type, 1);
        }
      }
      return next;
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatFieldName = (field) => field.replace('formula.', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());


  return (
    <>
      <style>{`        
        .remark-hover-card {
          position: fixed;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 0;
          z-index: 9999;
          min-width: 300px;
          max-width: 500px;
          pointer-events: none;
          animation: fadeIn 0.2s ease-in;
        }
        
        .remark-hover-card::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 20px;
          width: 12px;
          height: 12px;
          background: #fff;
          border-left: 1px solid #e5e7eb;
          border-top: 1px solid #e5e7eb;
          transform: rotate(45deg);
          z-index: -1;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
          font-weight: 700;
          color: #1f2937;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-radius: 8px 8px 0 0;
        }
        
        .remark-card-content {
          padding: 16px;
          font-size: 13px;
          color: #374151;
          line-height: 1.6;
          word-wrap: break-word;
          white-space: pre-wrap;
        }
      `}</style>
      <div className="p-6" id="cron-logs-container" style={{ position: "relative", minHeight: 200 }}>
        <Loader value={loading} variant="overlay" text="Loading cron logs..." />
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="rounded" />
              Auto-refresh
            </label>
            <button onClick={handleRefresh} className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-2">
              <FaSync className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={handleTriggerUpdate} disabled={pricingStatus?.isRunning} className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <FaPlay /> Run Pricing Update
            </button>
            <button onClick={handleSkuUpdate} disabled={pricingStatus?.isRunning} className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <FaBarcode /> Update SKUs
            </button>
            {pricingStatus?.isRunning && (
              <>
                <button onClick={() => handleStopJob(false)} className="px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center gap-2">
                  <FaStop /> Stop
                </button>
                <button onClick={() => handleStopJob(true)} className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2">
                  <FaUndo /> Stop & Rollback
                </button>
              </>
            )}
            {!pricingStatus?.isRunning && pricingStatus?.rollbackAvailable && (
              <button onClick={handleRollback} className="px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center gap-2">
                <FaUndo /> Rollback ({pricingStatus.rollbackCount})
              </button>
            )}
            <button onClick={handleCleanup} className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-2">
              <FaTrash /> Cleanup Old
            </button>
            <button onClick={handleDeleteAllLogs} className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2">
              <FaTrash /> Delete All
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow"><div className="text-gray-500 text-sm">Total Jobs</div><div className="text-2xl font-bold">{stats.totalJobs}</div></div>
            <div className="bg-blue-50 p-4 rounded-lg shadow"><div className="text-blue-600 text-sm">Running</div><div className="text-2xl font-bold text-blue-700">{stats.runningJobs}</div></div>
            <div className="bg-green-50 p-4 rounded-lg shadow"><div className="text-green-600 text-sm">Completed</div><div className="text-2xl font-bold text-green-700">{stats.completedJobs}</div></div>
            <div className="bg-red-50 p-4 rounded-lg shadow"><div className="text-red-600 text-sm">Failed</div><div className="text-2xl font-bold text-red-700">{stats.failedJobs}</div></div>
            <div className="bg-gray-50 p-4 rounded-lg shadow"><div className="text-gray-600 text-sm">Avg Duration</div><div className="text-2xl font-bold">{formatDuration(stats.avgDuration)}</div></div>
          </div>
        )}

        <div className="flex items-center gap-4 mb-4">
          <select value={pendingFilters.status} onChange={(e) => setPendingFilters((f) => ({ ...f, status: e.target.value }))} className="px-3 py-2 border rounded">
            <option value="">All Status</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={pendingFilters.parentJob || ""}
            onChange={(e) => setPendingFilters((f) => ({ ...f, parentJob: e.target.value, jobType: "" }))}
            className="px-3 py-2 border rounded"
          >
            <option value="">All Parent Jobs</option>
            {Object.entries(JOB_TYPE_GROUPS).map(([key, group]) => (
              <option key={key} value={key}>{group.label}</option>
            ))}
          </select>

          <select
            value={pendingFilters.jobType}
            onChange={(e) => setPendingFilters((f) => ({ ...f, jobType: e.target.value }))}
            className="px-3 py-2 border rounded"
            disabled={!pendingFilters.parentJob}
          >
            <option value="">All Sub-types</option>
            {pendingFilters.parentJob && JOB_TYPE_GROUPS[pendingFilters.parentJob].types.map(type => (
              <option key={type} value={type}>{JOB_TYPE_LABELS[type] || type} ({type})</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label htmlFor="startDateInput" className="text-sm font-medium text-gray-600">From:</label>
            <input
              id="startDateInput"
              type="date"
              value={pendingFilters.startDate ? pendingFilters.startDate.split('T')[0] : ""}
              onChange={(e) => {
                let val = e.target.value;
                if (val && !val.includes('T')) {
                  val += 'T00:00';
                }
                setPendingFilters((f) => ({ ...f, startDate: val }));
              }}
              className="px-3 py-2 border rounded text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="endDateInput" className="text-sm font-medium text-gray-600">To:</label>
            <input
              id="endDateInput"
              type="date"
              value={pendingFilters.endDate ? pendingFilters.endDate.split('T')[0] : ""}
              onChange={(e) => {
                let val = e.target.value;
                if (val && !val.includes('T')) {
                  val += 'T23:59';
                }
                setPendingFilters((f) => ({ ...f, endDate: val }));
              }}
              className="px-3 py-2 border rounded text-sm"
            />
          </div>
          <button
            onClick={() => {
              if ((pendingFilters.startDate && !pendingFilters.endDate) || (!pendingFilters.startDate && pendingFilters.endDate)) {
                setResultModal({
                  isOpen: true,
                  title: "Invalid Date Range",
                  message: "Please select both Start Date and End Date, or clear both to remove the date filter.",
                  type: "error"
                });
                return;
              }
              setPagination((p) => ({ ...p, page: 1 }));
              setFilters(pendingFilters);
              fetchLogs(true, pendingFilters);
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Apply
          </button>
          <button
            onClick={() => {
              const resetFilters = { status: "", parentJob: "", jobType: "", startDate: "", endDate: "" };
              setPagination((p) => ({ ...p, page: 1 }));
              setFilters(resetFilters);
              setPendingFilters(resetFilters);
              setExpandedDates(new Set());
              setExpandedTypes(new Set());
              // setNestedLogsCache({});
              fetchLogs(true, resetFilters);
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            Reset
          </button>
        </div>

        <div className="space-y-2">
          {availableDates.map((date) => (
            <div key={date} className="date-section bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
              <div
                onClick={() => toggleDate(date)}
                className="accordion-header py-2 px-4 flex items-center justify-between border-b bg-gray-50 font-bold text-gray-700 hover:bg-gray-100 transition-colors"
                title="Click to toggle date"
              >
                <span className="flex items-center gap-3">
                  <div className={`p-0.5 rounded bg-white border border-gray-200 shadow-sm transform transition-transform ${expandedDates.has(date) ? 'rotate-90' : ''}`}>
                    <FaPlay className="text-[8px] text-gray-400" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm">{new Date(date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    <span className="text-[10px] font-normal text-gray-400 uppercase tracking-tight">{date}</span>
                  </div>
                </span>
                <div className="flex items-center gap-4">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold">
                    {Object.keys(groupedLogs[date] || {}).length} Jobs
                  </span>
                </div>
              </div>

              {expandedDates.has(date) && (
                <div className="p-2 bg-gray-50 space-y-2">
                  {Object.entries(groupedLogs[date] || {}).map(([type, typeLogs]) => {
                    const typeKey = `${date}|${type}`;
                    const isExpanded = expandedTypes.has(typeKey);

                    // Per-jobType nested pagination meta (page, total, pages, limit)
                    const meta = nestedJobTypePages[typeKey] || {
                      page: 1,
                      total: typeLogs.length,
                      pages: 1,
                      limit: pagination.jobTypeLimit || typeLogs.length || 1
                    };

                    const itemsPerPage = meta.limit || pagination.jobTypeLimit || 5;
                    const paginatedLogs = typeLogs; // server already returns paginated slice
                    const currentPage = meta.page || 1;

                    // Use jobType counts from API response first, fallback to nested pagination data
                    const totalCount = jobTypeCountsByDate[date]?.[type] || meta.total || typeLogs.length;
                    // Get counter from the first log entry (main daily entry)
                    const counterValue = typeLogs.length > 0 ? (typeLogs[0].counter || 1) : 1;
                    const totalLocalPages = meta.pages || 1;
                    const isLoadingNested = nestedLoading[typeKey];

                    return (
                      <div key={typeKey} className="type-section border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
                        <div
                          onClick={() => {
                            toggleType(date, type);
                          }}
                          className={`py-1.5 px-3 flex items-center justify-between cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50 border-b border-blue-100' : 'hover:bg-gray-50'}`}
                        >
                          <span className="flex items-center gap-3 text-xs font-bold text-gray-600">
                            <FaPlay className={`text-[9px] transition-transform text-blue-500 ${isExpanded ? 'rotate-90' : ''}`} />
                            <div className="flex items-center gap-2">
                              <span>{JOB_TYPE_LABELS[type] || type}</span>
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[9px] font-mono border border-gray-200">
                                {type}
                              </span>
                            </div>
                          </span>
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-semibold border border-gray-100">
                            {counterValue} logs
                          </span>
                        </div>

                        {isExpanded && (
                          <div className="overflow-x-auto relative" onClick={(e) => e.stopPropagation()}>
                            {isLoadingNested && (
                              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                                <FaSpinner className="animate-spin text-blue-500 text-xl" />
                              </div>
                            )}
                            <table className="w-full text-[11px]">
                              <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Status</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Counter</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Triggered By</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">User</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Remark</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Progress</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Products</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Time</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-400 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {paginatedLogs.map((log) => (
                                  <tr key={log._id} className="hover:bg-gray-50/50 group">
                                    <td className="px-3 py-1.5">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_COLORS[log.status]}`}>
                                        {STATUS_ICONS[log.status]} {log.status}
                                      </span>
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold border border-blue-100">
                                        {log.counter || 1}
                                      </span>
                                    </td>
                                    <td className="px-3 py-1.5">
                                      {log.triggeredBy === 'algorithm-update' ? (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded text-[9px] uppercase font-bold border border-yellow-100">⚡ Algo</span>
                                      ) : (
                                        <span className="capitalize text-gray-500 font-medium">{log.triggeredBy}</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-1.5 text-gray-500 font-medium">
                                      {log.triggeredByUser?.name || log.triggeredByUser?.email || '-'}
                                    </td>
                                    <td className="px-3 py-1.5">
                                      {log.remark ? (
                                        <div
                                          className="remark-cell-container max-w-[150px]"
                                          onMouseEnter={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setHoveredRemark({
                                              remark: log.remark,
                                              top: rect.bottom + 8,
                                              left: rect.left,
                                            });
                                          }}
                                          onMouseLeave={() => setHoveredRemark(null)}
                                          style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                                        >
                                          <span className="remark-preview-text text-gray-400 text-[10px]">
                                            {log.remark.length > 25 ? log.remark.substring(0, 25) + '...' : log.remark}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className="text-gray-300">-</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <div className="w-16">
                                        <div className="text-[9px] font-bold text-gray-400 mb-0.5">{log.progress?.percent?.toFixed(0) || 0}%</div>
                                        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full transition-all duration-500 ${log.status === "failed" ? "bg-red-300" : log.status === "running" ? "bg-blue-300 animate-pulse" : "bg-green-300"}`}
                                            style={{ width: `${log.progress?.percent || 0}%` }}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <div className="flex flex-col">
                                        <span className="text-gray-600 font-bold">{log.stats?.updatedProducts || 0} / {log.stats?.totalProducts || 0}</span>
                                        {log.stats?.failedProducts > 0 && <span className="text-[8px] text-red-400 font-bold uppercase">{log.stats.failedProducts} Fail</span>}
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <div className="flex flex-col text-[10px]">
                                        <span className="text-gray-600 font-bold">{new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        <span className="text-gray-400 text-[9px]">{formatDuration(log.duration)}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                      <div className="flex gap-1.5 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => fetchLogDetails(log._id)} className="p-1 bg-blue-50 text-blue-500 rounded border border-blue-100 hover:bg-blue-500 hover:text-white transition-all" title="Details"><FaEye size={10} /></button>
                                        {log.status === 'running' && (
                                          <button onClick={() => handleStopJob(true)} className="p-1 bg-red-50 text-red-500 rounded border border-red-100 hover:bg-red-500 hover:text-white transition-all" title="Stop"><FaStop size={10} /></button>
                                        )}
                                        {log.status !== 'running' && (
                                          <button onClick={() => handleDeleteLog(log._id, log.status)} className="p-1 bg-gray-50 text-gray-400 rounded border border-gray-100 hover:bg-red-500 hover:text-white transition-all" title="Delete"><FaTrash size={10} /></button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {/* Nested Pagination Controls */}
                            {totalLocalPages > 1 && (
                              <div className="py-1.5 px-3 bg-gray-50/50 border-t border-gray-50 flex justify-between items-center">
                                <span className="text-[10px] text-gray-400 font-medium">
                                  {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
                                </span>
                                <div className="flex gap-1.5">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const newPage = Math.max(1, currentPage - 1);
                                      if (newPage !== currentPage) {
                                        fetchNestedLogs(date, type, newPage);
                                      }
                                    }}
                                    disabled={currentPage === 1}
                                    className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[9px] font-bold hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                  >
                                    Nested Prev
                                  </button>
                                  <span className="flex items-center px-1 text-[9px] font-bold text-gray-400">
                                    {currentPage}/{totalLocalPages}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const newPage = Math.min(totalLocalPages, currentPage + 1);
                                      if (newPage !== currentPage) {
                                        fetchNestedLogs(date, type, newPage);
                                      }
                                    }}
                                    disabled={currentPage === totalLocalPages}
                                    className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[9px] font-bold hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                  >
                                    Nested Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {availableDates.length === 0 && (
            <div className="bg-white p-12 text-center text-gray-400 rounded-lg shadow-sm border border-dashed border-gray-200">
              <FaClock className="text-4xl mx-auto mb-3 opacity-20" />
              <p className="font-medium">No cron logs found for your selection</p>
            </div>
          )}
        </div>

        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
            <span className="px-3 py-1">Page {pagination.page} of {pagination.pages}</span>
            <button onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        )}


        {/* Details Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b bg-gray-50">
                <h2 className="text-lg font-bold">Job Details</h2>
                <button onClick={() => setSelectedLog(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>
              {detailsLoading ? (
                <div className="p-8 text-center"><FaSpinner className="animate-spin text-3xl mx-auto" /></div>
              ) : (
                <div className="p-4 overflow-y-auto max-h-[calc(90vh-60px)]">
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div><div className="text-gray-500 text-sm">Job ID</div><div className="font-mono text-xs break-all">{selectedLog.jobId}</div></div>
                    <div><div className="text-gray-500 text-sm">Status</div><span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedLog.status]}`}>{STATUS_ICONS[selectedLog.status]} {selectedLog.status}</span></div>
                    <div><div className="text-gray-500 text-sm">Algorithm</div><div className="font-medium">{selectedLog.targetInfo?.algorithmName || "-"}</div></div>
                    <div><div className="text-gray-500 text-sm">Triggered By</div><div className="capitalize">{selectedLog.triggeredBy}</div></div>
                  </div>

                  {/* Algorithm Changes */}
                  {selectedLog.targetInfo?.algorithmChanges?.changedFields?.length > 0 && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 p-4 rounded-lg mb-6">
                      <h3 className="font-bold mb-3 text-yellow-800 flex items-center gap-2">⚡ Algorithm Changes Applied</h3>
                      <div className="bg-white rounded-lg p-3">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b"><th className="text-left py-2 text-gray-600">Field</th><th className="text-left py-2 text-gray-600">Previous</th><th className="text-left py-2 text-gray-600">New</th></tr></thead>
                          <tbody>
                            {selectedLog.targetInfo.algorithmChanges.changedFields.map((field, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="py-2 font-mono text-yellow-700">{formatFieldName(field)}</td>
                                <td className="py-2"><span className="bg-red-100 text-red-700 px-2 py-1 rounded line-through">{selectedLog.targetInfo.algorithmChanges.previousValues?.[field] ?? 'N/A'}</span></td>
                                <td className="py-2"><span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">{selectedLog.targetInfo.algorithmChanges.newValues?.[field] ?? 'N/A'}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {selectedLog.targetInfo?.skus?.length > 0 && (
                    <div className="bg-blue-50 p-4 rounded-lg mb-6">
                      <h3 className="font-medium mb-2 text-blue-800">Target SKUs ({selectedLog.targetInfo.skus.length})</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedLog.targetInfo.skus.slice(0, 20).map((sku, i) => (<span key={i} className="bg-white px-2 py-1 rounded text-sm font-mono">{sku}</span>))}
                        {selectedLog.targetInfo.skus.length > 20 && <span className="text-blue-600 text-sm">+{selectedLog.targetInfo.skus.length - 20} more</span>}
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h3 className="font-medium mb-3">Statistics</h3>
                    <div className="grid grid-cols-5 gap-4 text-sm">
                      <div className="bg-white p-3 rounded"><div className="text-gray-500">Total</div><div className="text-xl font-bold">{selectedLog.stats?.totalProducts || 0}</div></div>
                      <div className="bg-white p-3 rounded"><div className="text-gray-500">Processed</div><div className="text-xl font-bold">{selectedLog.stats?.processedProducts || 0}</div></div>
                      <div className="bg-white p-3 rounded"><div className="text-gray-500">Updated</div><div className="text-xl font-bold text-green-600">{selectedLog.stats?.updatedProducts || 0}</div></div>
                      <div className="bg-white p-3 rounded"><div className="text-gray-500">Failed</div><div className="text-xl font-bold text-red-600">{selectedLog.stats?.failedProducts || 0}</div></div>
                      <div className="bg-white p-3 rounded"><div className="text-gray-500">Variants</div><div className="text-xl font-bold">{selectedLog.stats?.updatedVariants || 0}</div></div>
                    </div>
                  </div>

                  {selectedLog.logs?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-medium mb-3">Activity Logs</h3>
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm max-h-48 overflow-y-auto">
                        {selectedLog.logs.map((log, i) => (
                          <div key={i} className={log.level === "error" ? "text-red-400" : log.level === "warn" ? "text-yellow-400" : "text-gray-300"}>
                            [{new Date(log.timestamp).toLocaleTimeString()}] [{log.level.toUpperCase()}] {log.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedLog.errorRecords?.length > 0 && (
                    <div>
                      <h3 className="font-medium mb-3 text-red-600">Errors ({selectedLog.errorRecords.length})</h3>
                      <div className="bg-red-50 p-4 rounded-lg max-h-48 overflow-y-auto">
                        {selectedLog.errorRecords.map((err, i) => (
                          <div key={i} className="text-sm mb-2 pb-2 border-b border-red-100 last:border-0">
                            <span className="font-mono text-red-700">SKU: {err.sku || err.productId}</span>
                            <div className="text-red-600">{err.error}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <ConfirmModal isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} confirmText={confirmModal.confirmText} confirmColor={confirmModal.confirmColor} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal((m) => ({ ...m, isOpen: false }))} />
        <ResultModal isOpen={resultModal.isOpen} title={resultModal.title} message={resultModal.message} type={resultModal.type} onClose={() => setResultModal((m) => ({ ...m, isOpen: false }))} />

        {/* Run Update Modal - Algorithm/Domain Selection */}
        {runUpdateModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Run Pricing Update</h3>
                <button onClick={() => setRunUpdateModal((m) => ({ ...m, isOpen: false }))} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>

              <div className="space-y-4">
                {/* Domain Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Website/Domain</label>
                  <div className="grid grid-cols-1 gap-2">
                    {runUpdateModal.domains.map((domain) => (
                      <label key={domain} className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${runUpdateModal.selectedDomain === domain ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input
                          type="radio"
                          name="domain"
                          value={domain}
                          checked={runUpdateModal.selectedDomain === domain}
                          onChange={(e) => {
                            const newDomain = e.target.value;
                            const domainAlgos = runUpdateModal.algorithms.filter(a => a.domain === newDomain);
                            setRunUpdateModal((m) => ({ ...m, selectedDomain: newDomain, selectedAlgorithm: domainAlgos[0] || null }));
                          }}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium">{domain}</div>
                          <div className="text-xs text-gray-500">
                            {runUpdateModal.algorithms.filter(a => a.domain === domain).length} algorithm(s) available
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Algorithm Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Algorithm</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {runUpdateModal.algorithms
                      .filter(a => a.domain === runUpdateModal.selectedDomain)
                      .map((algo) => (
                        <label key={algo._id} className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${runUpdateModal.selectedAlgorithm?._id === algo._id ? 'bg-blue-50 border border-blue-500' : 'bg-gray-50 hover:bg-gray-100'}`}>
                          <input
                            type="radio"
                            name="algorithm"
                            checked={runUpdateModal.selectedAlgorithm?._id === algo._id}
                            onChange={() => setRunUpdateModal((m) => ({ ...m, selectedAlgorithm: algo }))}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{algo.name}</div>
                            <div className="text-xs text-gray-500">
                              Markup: {(algo.formula?.markup_percent * 100 || 0).toFixed(0)}% |
                              RMB Rate: {algo.formula?.rmb_to_inr_rate || 'N/A'} |
                              Freight: ₹{algo.formula?.international_freight_per_kg || 'N/A'}/kg
                            </div>
                          </div>
                          {algo.priority > 0 && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Priority: {algo.priority}</span>}
                        </label>
                      ))}
                    {runUpdateModal.algorithms.filter(a => a.domain === runUpdateModal.selectedDomain).length === 0 && (
                      <div className="text-gray-500 text-sm p-3">No algorithms available for this domain</div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="font-medium mb-1">Summary</div>
                  <div className="text-gray-600">
                    Update all products for <span className="font-medium text-blue-600">{runUpdateModal.selectedDomain}</span>
                    {runUpdateModal.selectedAlgorithm ? (
                      <> using <span className="font-medium text-blue-600">"{runUpdateModal.selectedAlgorithm.name}"</span></>
                    ) : (
                      <span className="text-red-500"> - Please select an algorithm</span>
                    )}
                  </div>
                </div>

                {/* Remark */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remark / Reason <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={runUpdateModal.remark}
                    onChange={(e) => setRunUpdateModal((m) => ({ ...m, remark: e.target.value }))}
                    placeholder="e.g., Algorithm formula updated, RMB rate changed..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setRunUpdateModal((m) => ({ ...m, isOpen: false }))} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleRunSelectedUpdate}
                  disabled={!runUpdateModal.selectedAlgorithm}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPlay /> Start Update
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SKU Update Modal */}
        {skuUpdateModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <FaBarcode className="text-green-500" /> Update SKU Pricing
                </h3>
                <button onClick={() => setSkuUpdateModal((m) => ({ ...m, isOpen: false }))} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>

              <div className="space-y-4">
                {/* SKU Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter SKUs (comma, space, or newline separated)
                  </label>
                  <textarea
                    value={skuUpdateModal.skuInput}
                    onChange={(e) => handleSkuInputChange(e.target.value)}
                    placeholder="Enter SKUs here...&#10;e.g., 839685850406, 123456789&#10;or paste multiple SKUs"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows={5}
                  />
                  <div className="mt-2 text-sm text-gray-500">
                    {skuUpdateModal.skuList.length > 0 ? (
                      <span className="text-green-600 font-medium">{skuUpdateModal.skuList.length} SKU(s) detected</span>
                    ) : (
                      <span>No SKUs entered</span>
                    )}
                  </div>
                </div>

                {/* SKU Preview */}
                {skuUpdateModal.skuList.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">SKUs to update:</div>
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                      {skuUpdateModal.skuList.slice(0, 20).map((sku, i) => (
                        <span key={i} className="bg-white px-2 py-1 rounded text-sm font-mono border">{sku}</span>
                      ))}
                      {skuUpdateModal.skuList.length > 20 && (
                        <span className="text-gray-500 text-sm">+{skuUpdateModal.skuList.length - 20} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Algorithm Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Algorithm</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {skuUpdateModal.algorithms.map((algo) => (
                      <label key={algo._id} className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${skuUpdateModal.selectedAlgorithm?._id === algo._id ? 'bg-green-50 border border-green-500' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <input
                          type="radio"
                          name="skuAlgorithm"
                          checked={skuUpdateModal.selectedAlgorithm?._id === algo._id}
                          onChange={() => setSkuUpdateModal((m) => ({ ...m, selectedAlgorithm: algo }))}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{algo.name}</div>
                          <div className="text-xs text-gray-500">
                            Domain: {algo.domain} | Markup: {(algo.formula?.markup_percent * 100 || 0).toFixed(0)}%
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-green-50 p-3 rounded-lg text-sm border border-green-200">
                  <div className="font-medium mb-1 text-green-800">Summary</div>
                  <div className="text-green-700">
                    Update <span className="font-bold">{skuUpdateModal.skuList.length}</span> SKU(s)
                    {skuUpdateModal.selectedAlgorithm && (
                      <> using <span className="font-bold">"{skuUpdateModal.selectedAlgorithm.name}"</span></>
                    )}
                  </div>
                </div>

                {/* Remark */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remark / Reason <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={skuUpdateModal.remark}
                    onChange={(e) => setSkuUpdateModal((m) => ({ ...m, remark: e.target.value }))}
                    placeholder="e.g., Price correction, Customer request..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setSkuUpdateModal((m) => ({ ...m, isOpen: false }))} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleRunSkuUpdate}
                  disabled={skuUpdateModal.skuList.length === 0 || !skuUpdateModal.selectedAlgorithm}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPlay /> Update SKUs
                </button>
              </div>
            </div>
          </div>
        )}

        {hoveredRemark && (
          <div
            className="remark-hover-card"
            style={{
              top: hoveredRemark.top,
              left: hoveredRemark.left
            }}
          >
            <div className="remark-card-header bg-gray-50 border-b p-1.5 font-bold text-[9px] text-gray-400 uppercase tracking-widest">Remark</div>
            <div className="remark-card-content p-2 text-xs text-gray-600 border-none shadow-none">{hoveredRemark.remark}</div>
          </div>
        )}
      </div>
    </>
  );
}
